import React, { createContext, useContext, useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  admissionDenialMessage,
  admissionEntryForCurrentCaller,
  eventHasConcluded,
  getEndTime,
  getEventSession,
  isCurrentRoomEntry,
  getRelativeStartTime,
  entryUrlFromSessionShare
} from "../../utils/events-utils";
import {
  bookEventSession,
  fetchEvent,
  getEventAdmission,
  getSessionAdmission,
  getSessionShare,
  registerForEvent
} from "../../utils/event-service";
import configs from "../../utils/configs";
import { trackRsvpConfirmed } from "../../utils/conversion-tracking";
import getAuthUser from "../../utils/get-auth-user";
import { useModalContext } from "./ModalContext";
import { RegistrationConfirmedDialog } from "./RegistrationConfirmedDialog";
import { PresentationsSelectDialog } from "./PresentationsSelectDialog";
import { RSVPErrorDialog } from "./RSVPErrorDialog";

const EventContext = createContext({
  eventData: null,
  isLoading: true,
  error: null,
  presentationData: null,
  isPresentation: false,
  isInTheEvent: null,
  handleRSVP: null,
  handleAdmission: null
});

export const useEvent = () => useContext(EventContext);

export const EventContextProvider = ({
  children,
  slug,
  sessionSlug: roomSessionSlug,
  roomRole,
  performConditionalSignIn,
  store
}) => {
  const [eventData, setEventData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [presentationData, setPresentationData] = useState(null);
  const { showNonHistoriedDialog, closeDialog } = useModalContext();
  const registration = eventData?.current_user?.registration || null;
  const isInTheEvent = registration?.status === "confirmed";
  const hasActiveTicket = (eventData?.ticket_offers || []).length > 0;

  const refreshEvent = async () => {
    if (!slug) return null;
    setIsLoading(true);
    try {
      const data = await fetchEvent(slug);
      setEventData(data);
      setError(null);
      return data;
    } catch (fetchError) {
      setError(fetchError);
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (slug) refreshEvent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  useEffect(() => {
    setPresentationData(getEventSession(eventData, roomSessionSlug));
  }, [eventData, roomSessionSlug]);

  const showError = (reason, errorMessage) => {
    showNonHistoriedDialog(RSVPErrorDialog, {
      onClose: () => closeDialog && closeDialog(),
      reason,
      errorMessage: errorMessage || admissionDenialMessage(reason)
    });
  };

  const selectSessions = preselectedSlug =>
    new Promise(resolve => {
      showNonHistoriedDialog(PresentationsSelectDialog, {
        open: true,
        presentations: eventData?.presentations || [],
        preselectedSlug,
        onConfirm: slugs => {
          closeDialog && closeDialog();
          resolve(slugs);
        },
        onClose: () => {
          closeDialog && closeDialog();
          resolve([]);
        }
      });
    });

  const showRegistrationConfirmation = async () => {
    const dataForTime = presentationData || eventData;
    const relative = getRelativeStartTime(new Date(dataForTime?.start_time), new Date(dataForTime?.end_time));
    showNonHistoriedDialog(RegistrationConfirmedDialog, {
      open: true,
      eventName: eventData?.name,
      userEmail: (await getAuthUser(store))?.email,
      startsInText: relative?.replace("Starting in ", "") || null,
      onJoin: () => closeDialog && closeDialog(),
      onClose: () => closeDialog && closeDialog()
    });
  };

  const afterSignIn = (action, prompt) => {
    if (!performConditionalSignIn) {
      showError("authentication_required", "Sign in is required to continue.");
      return Promise.resolve(false);
    }

    return new Promise(resolve => {
      performConditionalSignIn(
        () => Boolean(window.APP?.hubChannel?.signedIn || store?.state?.authUser?.id),
        async () => {
          try {
            resolve(await action());
          } catch (actionError) {
            console.error("Event request failed", actionError);
            showError(actionError?.data?.code, actionError?.message);
            resolve(false);
          }
        },
        prompt,
        signInError => {
          console.error("Event sign-in failed", signInError);
          showError("authentication_required", signInError?.message);
          resolve(false);
        },
        { authorizationParams: { "ext-ui": "rsvp" } }
      );
    });
  };

  const handleRSVP = presentationSlug =>
    afterSignIn(async () => {
      const selectedSessionSlug = presentationSlug || presentationData?.slug;
      if (!registration || registration.status !== "confirmed") {
        const sessionSlugs = eventData?.presentations?.length ? await selectSessions(selectedSessionSlug) : [];
        if (eventData?.presentations?.length && sessionSlugs.length === 0) return false;
        const receipt = await registerForEvent({ eventSlug: slug, sessionSlugs });
        if (!receipt) throw new Error("Event registration was unavailable.");
        if (receipt.registration?.status !== "confirmed" || receipt.checkout) {
          await refreshEvent();
          showError(
            "registration_pending",
            "Registration is not confirmed yet. Complete any required payment to continue."
          );
          return false;
        }
        trackRsvpConfirmed(receipt.registration, slug, configs.GA4_GLOBAL_MEASUREMENT_ID);
      } else if (selectedSessionSlug) {
        const receipt = await bookEventSession({ eventSlug: slug, sessionSlug: selectedSessionSlug });
        if (!receipt) throw new Error("Presentation booking was unavailable.");
      }

      await refreshEvent();
      await showRegistrationConfirmation();
      return true;
    }, "Sign in to RSVP to this activation");

  const requestAdmission = sessionSlug =>
    afterSignIn(async () => {
      const targetSessionSlug = sessionSlug || roomSessionSlug;
      if (roomRole === "session" && !targetSessionSlug) {
        showError("session_entry_unavailable");
        return false;
      }
      const receipt = targetSessionSlug
        ? await getSessionAdmission({ eventSlug: slug, sessionSlug: targetSessionSlug })
        : await getEventAdmission({ eventSlug: slug });
      const entry = admissionEntryForCurrentCaller(receipt);
      if (!entry) {
        showError(receipt?.reason);
        return false;
      }

      return entry;
    }, "Sign in to join this activation");

  const handleAdmission = async sessionSlug => {
    const entry = await requestAdmission(sessionSlug);
    if (!entry) return false;

    window.location.assign(entry.url);
    return false;
  };

  const handleCurrentRoomAdmission = async () => {
    const entry = await requestAdmission(roomSessionSlug);
    if (!entry) return false;
    if (isCurrentRoomEntry(entry.url, window.location.href)) return true;

    window.location.assign(entry.url);
    return false;
  };

  const handleConcludedSessionVisit = async sessionSlug => {
    const share = await getSessionShare({ eventSlug: slug, sessionSlug });
    const entryUrl = entryUrlFromSessionShare(share);
    if (entryUrl) window.location.href = entryUrl;
    else showError("session_entry_unavailable");
    return false;
  };

  const isPresentation = Boolean(presentationData);
  const eventEnded = eventHasConcluded(getEndTime(presentationData || eventData));

  return (
    <EventContext.Provider
      value={{
        eventData,
        isLoading,
        error,
        slug,
        presentationData,
        isPresentation,
        isInTheEvent: eventEnded ? true : isInTheEvent,
        handleRSVP,
        handleAdmission,
        handleCurrentRoomAdmission,
        handleConcludedSessionVisit,
        hasActiveTicket
      }}
    >
      {children}
    </EventContext.Provider>
  );
};

EventContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
  slug: PropTypes.string,
  sessionSlug: PropTypes.string,
  roomRole: PropTypes.oneOf(["lobby", "session"]),
  performConditionalSignIn: PropTypes.func,
  store: PropTypes.object
};
